import { useMutation, useQuery } from '@tanstack/vue-query'

import type { ApiResponse } from '@/types/user'

import { useAxios } from '@/composables/use-axios'

const { axiosInstance } = useAxios()

// API完整域名配置
const API_BASE_URL_V2_1 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/`

/**
 * Tour Guide 参数
 */
export interface TourGuideParams {
  attribution_model: string
  conversion_goal: string
  date_range: string
  dimensions: string
}

/**
 * Tour Guide 记录
 */
export interface TourGuideRecord {
  team_id: number
  guide_type: string
  guide_status: number // 0: 未激活任务，1: 激活(需要处理)，2: 已完成
  total_guide_step: number
  current_guide_step: number
  params: TourGuideParams | null
  review_status: number // 0: 未review, 1: 已review, 2: 已skip
}

/**
 * Tour Guide 信息响应数据
 */
export interface TourGuideInfoResponse {
  Records: TourGuideRecord[]
}

/**
 * 完成引导请求数据
 */
export interface TourGuideCompleteRequest {
  type: string
}

/**
 * 完成评论请求数据
 */
export interface TourGuideReviewCompleteRequest {
  type: string
  review_status: number // 1: 已review, 2: 已skip
}

/**
 * 获取 Tour Guide 任务列表 - Vue Query Hook 版本
 */
export function useTourGuideInfo() {
  return useQuery({
    queryKey: ['tourGuideInfo'],
    queryFn: async (): Promise<TourGuideInfoResponse> => {
      const response = await axiosInstance.get<ApiResponse<TourGuideInfoResponse>>(
        `${API_BASE_URL_V2_1}tour/guide/info`,
      )
      return response.data?.data
    },
  })
}

/**
 * 获取 Tour Guide 任务列表 - 普通函数版本
 */
export async function tourGuideInfoApi(): Promise<TourGuideInfoResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<TourGuideInfoResponse>>(
    `${API_BASE_URL_V2_1}tour/guide/info`,
  )
  return response.data?.data
}

/**
 * 完成引导 - Vue Query Hook 版本
 */
export function useTourGuideComplete() {
  return useMutation({
    mutationKey: ['tourGuideComplete'],
    mutationFn: async (data: TourGuideCompleteRequest): Promise<TourGuideInfoResponse> => {
      const response = await axiosInstance.post<ApiResponse<TourGuideInfoResponse>>(
        `${API_BASE_URL_V2_1}tour/guide/complete`,
        data,
      )
      return response.data?.data
    },
  })
}

/**
 * 完成引导 - 普通函数版本
 */
export async function tourGuideCompleteApi(data: TourGuideCompleteRequest): Promise<ApiResponse<TourGuideInfoResponse>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<TourGuideInfoResponse>>(
    `${API_BASE_URL_V2_1}tour/guide/complete`,
    data,
  )
  return response.data
}

/**
 * 完成评论 - Vue Query Hook 版本
 */
export function useTourGuideReviewComplete() {
  return useMutation({
    mutationKey: ['tourGuideReviewComplete'],
    mutationFn: async (data: TourGuideReviewCompleteRequest): Promise<TourGuideInfoResponse> => {
      const response = await axiosInstance.post<ApiResponse<TourGuideInfoResponse>>(
        `${API_BASE_URL_V2_1}tour/guide/review-complete`,
        data,
      )
      return response.data?.data
    },
  })
}

/**
 * 完成评论 - 普通函数版本
 */
export async function tourGuideReviewCompleteApi(data: TourGuideReviewCompleteRequest): Promise<ApiResponse<TourGuideInfoResponse>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<TourGuideInfoResponse>>(
    `${API_BASE_URL_V2_1}tour/guide/review-complete`,
    data,
  )
  return response.data
}
